import React, { useCallback, useEffect, useRef } from 'react';
import { Box, Divider, ToggleButton, Tooltip, Typography, useTheme } from '@mui/material';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  Title as HeadingIcon,
  Notes as ParagraphIcon,
  FormatListBulleted as BulletIcon,
  FormatListNumbered as NumberedIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignJustify as AlignJustifyIcon,
  FormatClear as ClearIcon,
} from '@mui/icons-material';

export interface RichTextEditorProps {
  label?: string;
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}

interface ToolbarAction {
  key: string;
  title: string;
  icon: React.ReactNode;
  run: () => void;
}

/**
 * Lightweight rich-text editor for a small, predefined set of HTML formatting:
 * bold, italic, headings (h3), paragraphs, lists and text alignment (incl. justify).
 * Built on contentEditable + document.execCommand — no external dependencies.
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  label,
  value,
  onChange,
  placeholder,
  minHeight = 120,
}) => {
  const theme = useTheme();
  const editorRef = useRef<HTMLDivElement | null>(null);
  // Always holds the latest `value` so the callback ref can seed the DOM on
  // attach without changing identity (which would detach/reattach the node).
  const valueRef = useRef(value);
  valueRef.current = value;

  // Callback ref: fires the moment the contentEditable node attaches to the DOM,
  // so we can write the initial HTML into it immediately (a plain useEffect on a
  // MUI Box ref proved unreliable and left the editor blank on open).
  const attachEditor = useCallback((node: HTMLDivElement | null) => {
    editorRef.current = node;
    if (node) {
      node.innerHTML = valueRef.current || '';
    }
  }, []);

  // Sync later external `value` changes into the DOM. Comparing against the
  // element's own innerHTML means our own keystrokes — which the parent echoes
  // back unchanged — don't reset the caret to the start.
  useEffect(() => {
    const el = editorRef.current;
    if (el && value !== el.innerHTML) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    onChange(el.innerHTML);
  }, [onChange]);

  const exec = useCallback(
    (command: string, arg?: string) => {
      editorRef.current?.focus();
      document.execCommand(command, false, arg);
      emitChange();
    },
    [emitChange],
  );

  const actions: (ToolbarAction | 'divider')[] = [
    { key: 'bold', title: 'Удебелен (Bold)', icon: <BoldIcon fontSize="small" />, run: () => exec('bold') },
    { key: 'italic', title: 'Курсив (Italic)', icon: <ItalicIcon fontSize="small" />, run: () => exec('italic') },
    'divider',
    { key: 'h3', title: 'Заглавие (H3)', icon: <HeadingIcon fontSize="small" />, run: () => exec('formatBlock', '<h3>') },
    { key: 'p', title: 'Параграф', icon: <ParagraphIcon fontSize="small" />, run: () => exec('formatBlock', '<p>') },
    'divider',
    { key: 'ul', title: 'Списък с точки', icon: <BulletIcon fontSize="small" />, run: () => exec('insertUnorderedList') },
    { key: 'ol', title: 'Номериран списък', icon: <NumberedIcon fontSize="small" />, run: () => exec('insertOrderedList') },
    'divider',
    { key: 'left', title: 'Подравни вляво', icon: <AlignLeftIcon fontSize="small" />, run: () => exec('justifyLeft') },
    { key: 'center', title: 'Центрирай', icon: <AlignCenterIcon fontSize="small" />, run: () => exec('justifyCenter') },
    { key: 'justify', title: 'Двустранно (Justify)', icon: <AlignJustifyIcon fontSize="small" />, run: () => exec('justifyFull') },
    'divider',
    { key: 'clear', title: 'Изчисти форматирането', icon: <ClearIcon fontSize="small" />, run: () => exec('removeFormat') },
  ];

  const isEmpty = !value || value === '<br>' || value.trim() === '';

  return (
    <Box>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, pl: 0.5 }}>
          {label}
        </Typography>
      )}
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          overflow: 'hidden',
          '&:focus-within': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.25,
            p: 0.5,
            bgcolor: theme.palette.action.hover,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {actions.map((action, idx) =>
            action === 'divider' ? (
              <Divider key={`div-${idx}`} orientation="vertical" flexItem sx={{ mx: 0.25 }} />
            ) : (
              <Tooltip key={action.key} title={action.title} arrow>
                <ToggleButton
                  value={action.key}
                  size="small"
                  sx={{ border: 'none', p: 0.5 }}
                  onMouseDown={(e) => {
                    // Prevent the button from stealing selection/focus from the editor
                    e.preventDefault();
                    action.run();
                  }}
                >
                  {action.icon}
                </ToggleButton>
              </Tooltip>
            ),
          )}
        </Box>
        <Box
          sx={{
            position: 'relative',
            '& h3': { fontSize: '1.1rem', fontWeight: 600, margin: '8px 0' },
            '& p': { margin: '4px 0' },
            '& ul, & ol': { paddingLeft: '24px', margin: '4px 0' },
          }}
        >
          {isEmpty && placeholder && (
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ position: 'absolute', top: 12, left: 14, pointerEvents: 'none' }}
            >
              {placeholder}
            </Typography>
          )}
          <div
            ref={attachEditor}
            contentEditable
            suppressContentEditableWarning
            onInput={emitChange}
            onBlur={emitChange}
            style={{
              minHeight,
              padding: 12,
              outline: 'none',
              fontSize: '0.95rem',
              lineHeight: 1.5,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default RichTextEditor;
